import type { ComponentDocs } from '../component-docs.js';
import { ICON_NAMES, makeIcon, type IconName } from './icons.js';
// The search example draws its own filter box out of the library's own field, so it is registered
// wherever this example runs rather than relying on something else having imported it first.
import './text-field.js';

/**
 * What `Icons` says about itself.
 *
 * ONE VOCABULARY, AND NOBODY COULD LOOK AT IT. Every glyph any component draws comes from here.
 * They are built through the DOM — `createElementNS`, one child per path — rather than from markup
 * strings, so a strict content-security policy holds, and there is no icon package installed
 * anywhere in the repository: the paths are in the file. That is all fine, and it left one problem.
 * Until this page there was no way to SEE the vocabulary. A person who cannot find the mark they
 * need concludes there isn't one, and the second private icon set starts — which is the failure
 * this entry exists to prevent, so the examples below are the point of it rather than an
 * illustration of it.
 *
 * THE SHEET IS READ FROM THE VOCABULARY, NEVER TRANSCRIBED. Both examples enumerate `ICON_NAMES`
 * at runtime, so a glyph added tomorrow appears on this page tomorrow with nobody editing
 * anything. For the same reason, no count of them is written into the prose here — the second
 * example counts what it drew. A number in a sentence is a number that goes stale.
 *
 * WHY BOTH EXAMPLES DECLARE A HOST. This piece registers no element, so `placements` is `[]` and
 * there is nothing to place. Drawing a contact sheet is something a PAGE does with the vocabulary,
 * not something a placement can express — a placement carries stored values and this needs a loop.
 * So the example places the container the sheet goes in and the surrounding page fills it, and the
 * page says so.
 *
 * WHAT IS DECLARED WHERE, given there is no element. `inputs`, `events`, `slots` and `tokens` are
 * all `[]` and all four are true rather than unexamined: nothing is set on it, it raises nothing,
 * nothing goes inside it, and it reads no design token at all — every glyph strokes `currentColor`
 * and takes its colour from whatever draws it. `methods` is used for the module's exported
 * functions, which are how something else drives this piece; they are not element methods, because
 * there is no element for them to be on.
 *
 * ON THE LAYOUT. The sheet uses the reference page's own `cl-glyphs` / `cl-glyph` grid classes —
 * the same ones the previous hand-kept example used — because how a documentation sheet is laid
 * out belongs to the page showing it. Those rules must stay in the page's stylesheet; without them
 * the sheet still lists every mark under its name, just unarranged.
 */

/** One mark, named under itself — the name is what a component author has to type. */
function glyphCell(name: IconName): HTMLElement {
  const cell = document.createElement('figure');
  cell.className = 'cl-glyph';
  const caption = document.createElement('figcaption');
  caption.textContent = name;
  cell.append(makeIcon(name), caption);
  return cell;
}

/** A grid of them. */
function glyphSheet(names: readonly IconName[]): HTMLElement {
  const sheet = document.createElement('div');
  sheet.className = 'cl-glyphs';
  for (const name of names) sheet.append(glyphCell(name));
  return sheet;
}

const docs: ComponentDocs = {
  aliases: ['icon', 'icons', 'glyph', 'symbol', 'icon set', 'icon library', 'svg icon', 'lucide'],

  answers: [
    'see every mark we already have',
    'find the icon for a thing by name',
    'put a glyph beside a label',
    'work out what to call the mark I want',
    'add a mark that does not exist yet',
    'make an icon take the colour of what it sits in',
  ],

  // Nowhere. It registers no element, so there is nothing to place — the vocabulary is called from
  // inside other components rather than dropped on a page.
  placements: [],

  // Nothing is set on it: it is a module of builders, not an element.
  inputs: [],
  events: [],
  state: [],

  slots: [],

  // None, and this is a deliberate design property rather than an omission. Every glyph is stroked
  // with `currentColor`, so it inherits the colour of whatever draws it and a component tints its
  // own glyph with its own token. Nothing here needs to know about a palette.
  tokens: [],

  // It registers no element and builds no shadow tree, so there is no inside to name.
  parts: [],

  methods: [
    {
      name: 'makeIcon',
      description:
        'Build one glyph and hand back a fresh `<svg>`. A fresh one each time, so the same mark can be drawn in ' +
        'several places at once. It is stroked with `currentColor` and sized by whatever holds it.',
      args: [{ name: 'name', type: 'IconName', description: 'A name from the vocabulary. The type is the list, so an unknown name does not compile.' }],
    },
    {
      name: 'intentIcon',
      description:
        'Build the glyph that stands for an intent — success, warning, danger, and info for anything else. ' +
        '`error` is accepted as an alias of danger. This is what the intent-carrying pieces call so that the same ' +
        'meaning gets the same mark everywhere.',
      args: [{ name: 'intent', type: 'string', description: 'The intent word. Anything unrecognised falls through to the info glyph rather than drawing nothing.' }],
    },
    {
      name: 'drawIcons',
      description:
        'Fill every `data-icon` placeholder under an element from this vocabulary. For a piece that builds its ' +
        'shadow tree from a markup template and so cannot call `makeIcon` mid-string: the template writes an ' +
        'empty `<span data-icon="log-out">` and this fills it afterwards, through the DOM. A name the vocabulary ' +
        'does not carry leaves the holder empty rather than drawing a blank box.',
      args: [{ name: 'scope', type: 'ParentNode', description: 'The subtree to fill — usually the shadow tree a component has just built.' }],
    },
    {
      name: 'isIconName',
      description:
        'Whether a string is a mark this vocabulary draws. For the case where the name arrives as data — from ' +
        'metadata, from a manifest — so an unknown one can be skipped rather than drawn blank.',
      args: [{ name: 'name', type: 'string', description: 'The candidate name.' }],
    },
  ],

  placing:
    'Not placed. A component imports `makeIcon` and appends the result inside its own shadow root, sized by its ' +
    'own stylesheet and coloured by whatever `color` is in force — which is why a glyph never needs a token of ' +
    'its own. The names live in one exported list, `ICON_NAMES`, and the type of a name IS that list, so a ' +
    'misspelling is a compile error rather than an empty square. SIZE is the same arrangement as colour: a glyph ' +
    'carries no width or height at all, only a 24-unit box to be drawn in, so it takes the size of whatever holds ' +
    'it. The holder a page author gets is `<caos-icon>`, whose box is a step of the platform size scale ' +
    '(`--caos-size-<step>-height`) — so no glyph has a size of its own to disagree with anybody else\'s.',

  whenToUse:
    'Whenever a component needs a glyph. It draws from this one vocabulary and never introduces its own — a ' +
    'second icon set is how the same idea ends up with two marks, and how a rebrand only half lands. If the mark ' +
    'you need is not here, ADD IT HERE rather than drawing it locally: one name, one path, one place, and every ' +
    'surface gets it.',

  insteadUse: [
    { name: 'StatusDot', when: 'The mark is standing for a state rather than a thing, and a coloured dot says it more plainly than a glyph.' },
    { name: 'Tag', when: 'The thing needs a word beside it, not a mark on its own.' },
    { name: 'ProvenanceMarker', when: 'The mark is saying where a value came from — that vocabulary is already chosen and carries its own meaning.' },
  ],

  accessibility:
    'A glyph carries no text and no title, so it is DECORATIVE by default and the caller is responsible for that ' +
    'being true. Mark the element holding it `aria-hidden="true"` and make sure the words are somewhere a screen ' +
    'reader can reach them — the label beside it, or an accessible name on the control. An icon-only control needs ' +
    'that name explicitly; every piece in this library that draws a bare glyph gives it one. What the vocabulary ' +
    'does handle for you is contrast: `currentColor` means a mark can never end up a colour its surroundings did ' +
    'not choose.',

  provenance:
    '**Verified 2026-09-24 — checked against every workspace manifest, which names no icon dependency.** ' +
    'No icon package is installed anywhere in the platform and none is shipped; what is here is path data, in ' +
    'one file, built through the DOM. Many of these outlines are lucide\'s own, typed in; the rest are drawn ' +
    'in the same idiom so that one vocabulary reads as one vocabulary. lucide is published under the ISC ' +
    'Licence — Copyright (c) 2026 Lucide Icons and Contributors, carrying forward Copyright (c) 2013-present ' +
    'Cole Bemis from Feather. ISC permits use and redistribution and requires that the copyright notice and the ' +
    'permission notice appear in all copies, which is why they are recorded here and beside the paths ' +
    'themselves rather than inferred from the visual resemblance; the full text is at ' +
    'https://github.com/lucide-icons/lucide/LICENSE. Nothing here is taken from any incumbent product\'s icon ' +
    'set. Where a surface wants a CONCEPT that also appears in one of theirs — a trend, a score, a strength ' +
    'meter — the concept belongs to nobody and we draw our own mark or adopt a freely licensed one. It is the ' +
    'artwork that would be theirs, never the idea.',

  examples: [
    {
      key: 'sheet',
      label: 'Every mark, named',
      summary:
        'The whole vocabulary drawn from the list itself, each mark under the name you would type to get it — so a ' +
        'glyph added to the list appears here without anybody editing this page.',
      placement: { component: 'div', inputs: { 'data-glyph-sheet': 'all' } },
      host: {
        summary:
          'draws the sheet — one cell per name in the vocabulary, read at the moment the page renders. A placement ' +
          'stores values; enumerating a list is not something it can carry.',
        run: (stage) => {
          const holder = stage.querySelector<HTMLElement>('[data-glyph-sheet="all"]');
          if (holder) holder.replaceChildren(glyphSheet(ICON_NAMES));
        },
      },
    },
    {
      key: 'find',
      label: 'Find one by name',
      summary:
        'The same sheet with a filter over it, because the NAME is what a component author has to type and a wall ' +
        'of marks does not tell you one. Type a fragment — "chevron", "trend", "layout" — and see what matches.',
      host: {
        summary:
          'draws the filter box and re-draws the sheet as you type, counting the matches against the size of the ' +
          'vocabulary rather than against a number written down anywhere.',
        run: (stage) => {
          const holder = stage.querySelector<HTMLElement>('[data-glyph-sheet="find"]');
          if (!holder) return;

          const box = document.createElement('caos-text-field');
          box.setAttribute('label', 'Find a mark by name');
          box.setAttribute('placeholder', 'chevron, folder, trend…');

          const count = document.createElement('p');
          const sheet = document.createElement('div');
          sheet.className = 'cl-glyphs';

          const paint = (raw: string): void => {
            const query = raw.trim().toLowerCase();
            const matches = ICON_NAMES.filter((name) => name.includes(query));
            sheet.replaceChildren(...matches.map(glyphCell));
            count.textContent = query
              ? `${matches.length} of ${ICON_NAMES.length} marks match “${raw.trim()}”.`
              : `${ICON_NAMES.length} marks in the vocabulary.`;
          };

          box.addEventListener('input', () => paint(box.value));
          paint('');
          holder.replaceChildren(box, count, sheet);
        },
      },
      placement: { component: 'div', inputs: { 'data-glyph-sheet': 'find' } },
    },
  ],
};

export default docs;
